import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import {
  SchemaDefinition,
  HostMetricDefinition,
} from "../interfaces/TopicSchema";

const configuredApi = import.meta.env.VITE_API_BASE_URL as string | undefined;
export const apiUrl =
  configuredApi && configuredApi.trim()
    ? configuredApi
    : typeof window !== "undefined"
      ? window.location.origin
      : "";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(dateString: string) {
  const date = new Date(dateString);
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return date.toLocaleString(navigator.languages[0], {
    timeZone: userTimeZone,
    hour12: false,
    year: "numeric",
    month: "numeric",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function formatDuration(startEpoch: number, endEpoch: number): string {
  let duration = Math.max(0, endEpoch - startEpoch) / 1e3;
  return formatDurationFromSeconds(duration);
}

export function formatDurationFromSeconds(duration: number): string {
  const hours = Math.floor(duration / 3600);
  duration %= 3600;
  const minutes = Math.floor(duration / 60);
  const seconds = duration % 60;

  return [
    hours > 0 ? `${hours}h` : "",
    minutes > 0 ? `${minutes}m` : "",
    seconds > 0 ? `${Math.floor(seconds)}s` : "",
  ]
    .filter(Boolean)
    .join(" ");
}

/**
 * Convert bytes to a human readable string with SI units (KB/MB/GB...).
 * - Returns '-' when input is undefined or null (convenient for optional values)
 * - Uses SI 1000-based units (KB = 1000 bytes) to match server and UI expectations
 */
export function formatBytes(bytes?: number | null, decimals = 2): string {
  if (bytes === undefined || bytes === null) return "-";
  if (bytes === 0) return "0 Bytes";

  const k = 1000; // SI units
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));
  const value = bytes / Math.pow(k, i);
  return `${parseFloat(value.toFixed(dm))} ${sizes[i]}`;
}

type DebouncedFunction<F extends (...args: any[]) => any> = (
  ...args: Parameters<F>
) => void;
export const debounce = <F extends (...args: any[]) => any>(
  func: F,
  delay: number,
): DebouncedFunction<F> => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<F>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
      timeoutId = null; // Clear timeoutId after function execution
    }, delay);
  };
};

/**
 * Live-monitoring status the agent reports for a discovered topic. The agent
 * owns the rules; the API resolves a status for every topic node (falling back
 * to a type rule for older agents), so the UI only renders it.
 */
export interface TopicMonitoring {
  supported: boolean;
  /** Why `supported` is false, e.g. "unsupported_type". */
  reason?: string;
  /** The agent's per-message size cap. */
  max_message_bytes?: number;
  /** Set while the agent drops messages over the cap: the largest size seen. */
  oversize_message_bytes?: number;
  /** "all" when every message is dropped, "some" when smaller ones get through. */
  oversize_scope?: "all" | "some";
}

export interface ArboristNodeData {
  id: string;
  name: string;
  topicName: string;
  sessionId?: string; // Optional session ID for context
  children?: ArboristNodeData[];
  isLeaf?: boolean;
  schemaId?: number;
  fieldPath?: string[];
  fieldType?: string;
  /** Present on topic nodes of discovered sources. */
  monitoring?: TopicMonitoring;
}

// Inside transformSchemaDefinition
function transformSchemaDefinition(
  definitions: SchemaDefinition[] | undefined,
  topicName: string,
  // parentSchemaId: number, // Renamed for clarity
  parentPath: string[] = [],
): ArboristNodeData[] {
  if (!definitions) {
    return [];
  }
  return definitions.map((def) => {
    const currentPath = [...parentPath, def.name];
    const nodeId = `${topicName}::${currentPath.join("/")}`;
    // Handle array type display in name or type? Let's keep name simple here.
    const nodeName =
      def.name +
      (def.isArray
        ? def.arraySize && def.arraySize > 0
          ? `[${def.arraySize}]`
          : `[]`
        : ""); // Simplified name

    // Determine field type string
    let fieldTypeStr = def.type;
    if (def.isArray) {
      fieldTypeStr = `array<${def.type}>`; // e.g., array<int32>
    }

    const baseData = {
      id: nodeId,
      name: nodeName, // Use simplified name
      topicName: topicName,
      // schemaId: parentSchemaId, // Schema this field belongs to
      fieldPath: currentPath,
      fieldType: fieldTypeStr, // <--- Assign the type string
    };

    if (!def.children || def.children.length === 0) {
      // Leaf node
      return { ...baseData, isLeaf: true };
    } else {
      // Branch node (object/nested type)
      return {
        ...baseData,
        isLeaf: false,
        children: transformSchemaDefinition(
          def.children,
          topicName,
          // If nested type, its schema name *is* its type name usually
          // def.type, // Pass the nested type name as the new 'parentSchemaName' for children
          currentPath,
        ),
      };
    }
  });
}

export interface DiscoveredTopics {
  topic_name: string;
  schema_id: number;
  schema_name: string;
  definition: SchemaDefinition[] | HostMetricDefinition;
}

export function transformDiscoveredTopicsToTreeData(
  discoveredSources: DiscoveredTopics[],
): ArboristNodeData[] {
  return discoveredSources.map((source) => {
    const topicNodeId = `${source.topic_name}`;
    const children = source.schema_name.startsWith("telegraf/group/")
      ? []
      : transformSchemaDefinition(
          source.definition as SchemaDefinition[],
          source.topic_name,
          // source.schema_name,
        );
    return {
      id: topicNodeId,
      name: source.topic_name, // Topic name
      children: children.length === 0 ? undefined : children,
      isLeaf: children.length === 0,
      topicName: source.topic_name,
      schemaId: source.schema_id, // Schema ID
      schemaName: source.schema_name, // Schema this topic uses
      fieldType: source.schema_name, // <--- Assign type for topic node
    };
  });
}
